import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { HeaderNotification } from '@/components/AppHeader.tsx';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type ApiNotification,
  type NotificationListResponse,
} from '@/libs/api/notificationService';

export const NOTIFICATIONS_QUERY_KEY = ['notifications'] as const;

type UseNotificationsOptions = {
  enabled: boolean;
  onNavigateToApplication: (applicationId: string) => void;
};

const formatNotificationTime = (createdAt: string): string => {
  const timestamp = new Date(createdAt).getTime();
  if (Number.isNaN(timestamp)) {
    return '';
  }

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60_000));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;

  return new Date(createdAt).toLocaleDateString();
};

const mapNotificationToHeaderItem = (notification: ApiNotification): HeaderNotification => ({
  id: notification.id,
  title: notification.title,
  description: notification.message,
  time: formatNotificationTime(notification.created_at),
  unread: !notification.is_read,
  applicationId: notification.application_id ?? null,
});

const updateSingleNotificationInCache = (
  previous: NotificationListResponse | undefined,
  updated: ApiNotification,
): NotificationListResponse | undefined => {
  if (!previous) {
    return previous;
  }

  const hadUnread = previous.items.some((item) => item.id === updated.id && !item.is_read);

  return {
    ...previous,
    items: previous.items.map((item) => (item.id === updated.id ? updated : item)),
    unread_count: hadUnread ? Math.max(0, previous.unread_count - 1) : previous.unread_count,
  };
};

const markAllNotificationsReadInCache = (
  previous: NotificationListResponse | undefined,
): NotificationListResponse | undefined => {
  if (!previous) {
    return previous;
  }

  const readAt = new Date().toISOString();

  return {
    ...previous,
    items: previous.items.map((item) => ({
      ...item,
      is_read: true,
      read_at: item.read_at ?? readAt,
    })),
    unread_count: 0,
  };
};

export const useNotifications = ({ enabled, onNavigateToApplication }: UseNotificationsOptions) => {
  const queryClient = useQueryClient();

  const { data: notificationsResponse } = useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    enabled,
    queryFn: () => listNotifications({ page: 1, page_size: 20 }),
    refetchInterval: 10_000,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });

  const markNotificationReadMutation = useMutation({
    mutationFn: markNotificationRead,
  });

  const markAllNotificationsReadMutation = useMutation({
    mutationFn: markAllNotificationsRead,
  });

  const notifications = useMemo<HeaderNotification[]>(
    () => notificationsResponse?.items.map(mapNotificationToHeaderItem) ?? [],
    [notificationsResponse],
  );

  const handleNotificationClick = useCallback(
    async (notification: HeaderNotification) => {
      try {
        if (notification.unread) {
          const updated = await markNotificationReadMutation.mutateAsync(notification.id);
          queryClient.setQueryData<NotificationListResponse | undefined>(
            NOTIFICATIONS_QUERY_KEY,
            (previous) => updateSingleNotificationInCache(previous, updated),
          );
        }

        if (notification.applicationId) {
          onNavigateToApplication(notification.applicationId);
        }
      } catch (error) {
        console.error('Failed to mark notification as read', error);
      } finally {
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      }
    },
    [markNotificationReadMutation, onNavigateToApplication, queryClient],
  );

  const handleMarkAllNotificationsRead = useCallback(async () => {
    try {
      await markAllNotificationsReadMutation.mutateAsync();
      queryClient.setQueryData<NotificationListResponse | undefined>(
        NOTIFICATIONS_QUERY_KEY,
        (previous) => markAllNotificationsReadInCache(previous),
      );
    } catch (error) {
      console.error('Failed to mark all notifications as read', error);
    } finally {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    }
  }, [markAllNotificationsReadMutation, queryClient]);

  return {
    notifications,
    handleNotificationClick,
    handleMarkAllNotificationsRead,
    isMarkingAllAsRead: markAllNotificationsReadMutation.isPending,
  };
};
